/**
 * Vuex store for the AI RFI import flow (LSO-717). Thin wrappers over the
 * /rfi-import endpoints. Actions surface backend errors by throwing so the
 * modal can display the message (conversion errors land on the polled job).
 */
export const state = () => ({
  jobs: []
})

export const getters = {
  getImportJobs: state => state.jobs || [],
  // `applying` counts as pending too: the server only reverts a stale applying job
  // (restart mid-apply) inside the LIST endpoint, so if nothing polls the list while
  // a job is applying, a stuck one never refreshes on the dashboard.
  hasPendingImportJobs: state =>
    (state.jobs || []).some(j => ['extracting', 'converting', 'applying'].includes(j.status))
}

export const mutations = {
  // Logout / switching users (global/logoutInit, loginInit): the job list is org-scoped
  // and must not carry over to the next account.
  init(state) {
    state.jobs = []
  },
  setImportJobs(state, jobs) {
    state.jobs = jobs || []
  }
}

export const actions = {
  // Fetch the user's recent import jobs (used by the status watcher for notifications).
  async fetchImportJobs({ commit }) {
    const jobs = await this.$axios.$get('/rfi-import/jobs')
    commit('setImportJobs', jobs || [])
    return jobs
  },

  // Upload a file and kick off an async import job. Returns { jobId, status }.
  // `sheets` (optional) restricts an Excel import to the named sheets; `pages`
  // (optional) restricts a PDF import to those 1-based page numbers. Omit/empty to
  // let the server read the whole workbook / document.
  // Each import spends 1 of the org's A.I. import tokens.
  async startImport(_ctx, { file, sheets, pages } = {}) {
    const data = new FormData()
    data.append('file', file)
    if (Array.isArray(sheets) && sheets.length) {
      data.append('sheets', JSON.stringify(sheets))
    }
    if (Array.isArray(pages) && pages.length) {
      data.append('pages', JSON.stringify(pages))
    }
    return await this.$axios.$post('/rfi-import/jobs', data, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })
  },

  // Remaining A.I. import tokens for the caller's org.
  async fetchImportTokens() {
    const res = await this.$axios.$get('/rfi-import/tokens')
    return (res && res.tokens) || 0
  },

  // Super admin: A.I. import cost roll-up across all orgs. `from`/`to` are optional
  // YYYY-MM-DD bounds (inclusive).
  async fetchUsageReport(_ctx, { from, to } = {}) {
    const params = {}
    if (from) { params.from = from }
    if (to) { params.to = to }
    return await this.$axios.$get('/rfi-import/usage', { params })
  },

  // Poll a job for status + draft.
  async fetchJob(_ctx, jobId) {
    return await this.$axios.$get(`/rfi-import/jobs/${jobId}`)
  },

  // Save edited draft questions.
  // `revision` is the draftRevision the page last loaded/saved; a stale one gets a 409.
  async saveDraft(_ctx, { jobId, questions, revision }) {
    return await this.$axios.$patch(`/rfi-import/jobs/${jobId}`, { questions, revision })
  },

  // Approve — create a new template or add to an existing one. Returns { templateId }.
  async applyJob(_ctx, { jobId, newTemplateName, targetTemplateId }) {
    return await this.$axios.$post(`/rfi-import/jobs/${jobId}/apply`, {
      newTemplateName,
      targetTemplateId
    })
  },

  // Re-run a failed import from the preserved original file (no re-upload needed).
  // Returns { jobId, status } with the job back in processing.
  async retryJob(_ctx, jobId) {
    return await this.$axios.$post(`/rfi-import/jobs/${jobId}/retry`)
  },

  // Cancel / discard a draft.
  async discard(_ctx, jobId) {
    return await this.$axios.$delete(`/rfi-import/jobs/${jobId}`)
  },

  // Per-tag corpus facts — { types, loisFields }, both keyed by tagKey. The review page
  // re-evaluates tag/type conflicts and the linked property listing field live with these.
  async fetchTagMeta() {
    return await this.$axios.$get('/rfi-import/tag-meta')
  },

  // Existing tags for the review page's autocomplete. searchTags returns tags from
  // every owner EXCEPT the one passed, so the jobId (a valid ObjectId, never a
  // question owner) yields the full set. The :type param is unused server-side.
  async fetchTagSuggestions(_ctx, jobId) {
    return await this.$axios.$get(`/tags/${jobId}/0`)
  },

  // Templates the user can add an import to (review page target chooser). Kept here
  // (not templates/fetchTemplateList) because that action reads/writes the templates
  // page's shared list state.
  // Newest first: the server's default sort is `created` ASCENDING, so without `sortby`
  // the 200-cap returns the OLDEST templates and a busy org's newest never appear.
  async fetchTemplateChoices() {
    const res = await this.$axios.$get('/templates?limit=200&sortby=-created')
    return (res && res.templates) || []
  }
}
